// The comparison staff read before attaching an existing Platforma365 person
// to one the provider refused as a duplicate (DEV-126). Pure, for the tests.

import type { PersonComparison } from '../api/customers.api.js';

export const PROVIDER_PERSON_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Mirrors the API: the reason is written to the case file. */
export const MIN_ATTACH_REASON_LENGTH = 10;

export type ComparisonStatus = 'match' | 'mismatch' | 'swapped' | 'not-compared';

export interface ComparisonRow {
  readonly label: string;
  readonly applicant: string;
  readonly provider: string;
  readonly status: ComparisonStatus;
  /** What staff should know about this row, e.g. the day/month swap. */
  readonly note: string;
}

const NONE = '—';

export function comparisonRows(comparison: PersonComparison): ComparisonRow[] {
  const dobBoth = comparison.applicantDateOfBirth !== '' && comparison.providerDateOfBirth !== '';
  const dobStatus: ComparisonStatus = comparison.dateOfBirthMatch
    ? 'match'
    : comparison.dateOfBirthSwapped
      ? 'swapped'
      : dobBoth
        ? 'mismatch'
        : 'not-compared';
  const dobNote =
    dobStatus === 'swapped'
      ? `Day and month look swapped: the provider holds ${comparison.providerDateOfBirth}, the application says ${comparison.applicantDateOfBirth}. Not a match — decide which is right.`
      : dobStatus === 'not-compared'
        ? 'One side holds no date of birth.'
        : '';

  const citizenshipStatus: ComparisonStatus = !comparison.citizenshipCompared
    ? 'not-compared'
    : comparison.citizenshipMatch
      ? 'match'
      : 'mismatch';

  const providerDocs = comparison.providerDocumentsMasked.join(', ');
  const documentStatus: ComparisonStatus = !comparison.documentCompared
    ? 'not-compared'
    : comparison.documentMatch
      ? 'match'
      : 'mismatch';

  return [
    {
      label: 'Date of birth',
      applicant: comparison.applicantDateOfBirth || NONE,
      provider: comparison.providerDateOfBirth || NONE,
      status: dobStatus,
      note: dobNote,
    },
    {
      label: 'Citizenship',
      applicant: comparison.applicantCitizenship || NONE,
      provider: comparison.providerCitizenship || NONE,
      status: citizenshipStatus,
      note: citizenshipStatus === 'not-compared' ? 'Not compared: one side states none.' : '',
    },
    {
      label: 'Passport / ID number',
      applicant: comparison.applicantDocumentMasked || NONE,
      provider: providerDocs || NONE,
      status: documentStatus,
      note:
        documentStatus === 'not-compared'
          ? comparison.providerDocumentsMasked.length === 0
            ? 'Not compared: the provider profile holds no identity document.'
            : 'Not compared: the application holds no document number.'
          : '',
    },
  ];
}

export const STATUS_LABEL: Readonly<Record<ComparisonStatus, string>> = {
  match: 'Matches',
  mismatch: 'Different',
  swapped: 'Day/month swapped',
  'not-compared': 'Not compared',
};

export function canAttach(providerPersonId: string, reason: string): boolean {
  return (
    PROVIDER_PERSON_ID_RE.test(providerPersonId.trim()) &&
    reason.trim().length >= MIN_ATTACH_REASON_LENGTH
  );
}
